import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { X, MessageCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImg } from "../../lib/api";

export const ProductModal = ({ product, settings, onClose }) => {
  const gallery = product ? (product.images?.length ? product.images : [product.image_url]).filter(Boolean) : [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: gallery.length > 1 });
  const [selected, setSelected] = useState(0);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
    return () => emblaApi.off("select", onSel);
  }, [emblaApi]);

  if (!product) return null;
  const wa = settings?.whatsapp_number;
  const msg = encodeURIComponent(
    `Hi ${settings?.brand_name || "Trendy threads by Vee"}! I'm interested in "${product.name}"${
      product.price ? ` (${product.price})` : ""
    }. Is it available?`
  );
  const multi = gallery.length > 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="product-modal"
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-obsidian-2 border border-white/10 grid grid-cols-1 md:grid-cols-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="modal-close"
          className="absolute top-4 right-4 z-20 text-ink hover:text-champagne bg-black/40 p-2 transition-colors"
          aria-label="Close"
        >
          <X strokeWidth={1.5} />
        </button>

        {/* Gallery */}
        <div className="relative bg-obsidian-3">
          <div className="overflow-hidden aspect-[4/5] md:aspect-auto md:h-full" ref={emblaRef} data-testid="product-gallery">
            <div className="flex h-full">
              {gallery.map((src, i) => (
                <div className="relative flex-[0_0_100%] h-full" key={i}>
                  <img src={resolveImg(src)} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {multi && (
            <>
              <button
                onClick={scrollPrev}
                data-testid="gallery-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-ink p-2 hover:text-champagne transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft strokeWidth={1.5} />
              </button>
              <button
                onClick={scrollNext}
                data-testid="gallery-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-black/50 text-ink p-2 hover:text-champagne transition-colors"
                aria-label="Next"
              >
                <ChevronRight strokeWidth={1.5} />
              </button>
              <div className="absolute bottom-4 inset-x-0 flex justify-center gap-2 z-10">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => scrollTo(i)}
                    data-testid={`gallery-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      selected === i ? "w-6 bg-champagne" : "w-1.5 bg-white/40"
                    }`}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-8 sm:p-12 flex flex-col justify-center">
          <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-champagne">
            {product.category}
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl text-ink mt-3 leading-none">
            {product.name}
          </h2>
          {product.price && (
            <p className="text-champagne text-xl mt-4 font-accent tracking-wide">{product.price}</p>
          )}
          <div className="w-12 h-px bg-white/15 my-7" />
          <p className="text-ash leading-relaxed tracking-wide">
            {product.description || "A signature piece from the collection."}
          </p>

          {wa && (
            <a
              href={`https://wa.me/${wa}?text=${msg}`}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-inquiry-button"
              className="mt-10 inline-flex items-center justify-center gap-3 bg-champagne text-obsidian font-accent text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-champagne-hover transition-colors duration-300"
            >
              <MessageCircle size={18} strokeWidth={2} /> Enquire on WhatsApp
            </a>
          )}
          <p className="text-ash/50 text-xs mt-4 tracking-wide">
            Orders &amp; enquiries handled personally via WhatsApp.
          </p>
        </div>
      </div>
    </div>
  );
};
